<!-- Wrapper with scroll container (standalone mode) -->
@if (enableScrollContainer()) {
<div #scrollArea (scroll)="onScroll($event)" class="flex-1 overflow-y-auto p-4 space-y-4 bg-white" [ngClass]="[
    scrollContainerClass() || 'h-[calc(100vh-7rem)]',
    !disableDarkTheme() ? 'dark:bg-surface-1' : ''
  ]">
  <ng-container *ngTemplateOutlet="messageContent"></ng-container>

  <!-- Navigation to next message or scroll down -->
  @if (!isUserNearBottom() || (sequentialRevelation() && hasPendingRevelations())) {
  <div class="sticky bottom-6 flex justify-center z-10">
    <button (click)="handleScrollDownClick()"
      class="flex items-center justify-center w-10 h-10 bg-gray-500 text-white rounded-full shadow-lg cursor-pointer border-2 border-white opacity-100 transition-transform hover:scale-105 active:scale-95">
      <app-icon-v2 name="arrowDown" class="w-5 h-5 text-white" />
    </button>
  </div>
  }

  <ng-content select="[footer]"></ng-content>
</div>
} @else {
<!-- Direct content without scroll wrapper (embedded mode) -->
<div class="flex flex-col gap-4">
  <ng-container *ngTemplateOutlet="messageContent"></ng-container>
</div>
}

<!-- Message content template -->
<ng-template #messageContent>
  @if (isLoadingHistory()) {
  <div class="flex justify-center py-2">
    <div class="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
  }

  @for (msg of displayedMessages(); track $index) {
  @if (isNewDay($index, displayedMessages())) {
  <div class="flex items-center justify-center my-4">
    <span class="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{{ getDateLabel(msg.timestamp)
      }}</span>
  </div>
  }

  <div [id]="'msg-' + $index">
    @if (msg.from === 'ai') {
    <!-- AI Message -->
    <div class="flex items-start gap-3">
      <div
        class="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105">
        <app-icon-v2 name="bot" class="text-white w-6 h-6" />
      </div>
      <div class="flex-1">
        <div class="flex items-center gap-2 mb-1">
          <span class="text-sm font-semibold text-[#4F46E5]"
            [ngClass]="{'dark:text-purple-400': !disableDarkTheme()}">AI Assistant</span>
          @if(msg.timestamp) {
          <span class="text-[10px] text-gray-400 font-medium ml-1">{{ msg.timestamp | date:'shortTime' }}</span>
          }
        </div>
        <div
          class="bg-[#F5F3FF] rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-[#DDD6FE] transition-all hover:shadow-md markdown-content"
          [ngClass]="{
            'dark:bg-surface-2': !disableDarkTheme(),
            'dark:border-slate-700': !disableDarkTheme()
        }">
          @for (segment of getMessageSegments(msg.text); track $index) {
          @if (segment.type === 'text') {
          <markdown [data]="formatMessage(segment.content)" class="text-sm text-[#1E1B4B] leading-relaxed"
            [ngClass]="{'dark:text-white': !disableDarkTheme()}">
          </markdown>
          } @else if (segment.type === 'chart') {
          <div class="my-4 chart-segment w-full">
            @if (layoutMode() === 'fullscreen') {
            @if (segment.content.type === 'line') {
            <div class="h-[220px] w-full">
              <app-line-chart [data1]="getSeriesData(segment.content.series, 0)"
                [data2]="getSeriesData(segment.content.series, 1)" [data3]="getSeriesData(segment.content.series, 2)"
                [data4]="getSeriesData(segment.content.series, 3)" [data5]="getSeriesData(segment.content.series, 4)"
                [valueKey]="'value'" [unit]="'day'" [showXAxis]="true" [showYAxis]="true" [showYAxisGrid]="true"
                [curveType]="'curveMonotoneX'" [marginProps]="{ top: 20, right: 30, bottom: 30, left: 40 }">
              </app-line-chart>

              <!-- Custom Legend for Line Chart -->
              <div class="flex flex-wrap items-center justify-center gap-4 mt-2">
                @for (s of segment.content.series; track s.key) {
                <div class="flex items-center gap-2">
                  <span class="w-3 h-0.5 rounded-full" [style.backgroundColor]="s.isLine ? '#8b5cf6' : s.color"></span>
                  <span class="text-[10px] font-medium text-slate-500">{{ s.name }}</span>
                </div>
                }
              </div>
            </div>
            } @else {
            <app-comprehensive-bar-chart [series]="segment.content.series"
              [mode]="segment.content.series.length > 1 ? 'multiple' : 'single'" [orientation]="'vertical'"
              [showCard]="false" [height]="260" [barRadius]="8" [xAxis]="{ show: true, showGrid: false }"
              [yAxis]="{ show: true, showGrid: true, gridStyle: 'dashed' }"
              [legend]="{ show: segment.content.series.length > 1, position: 'bottom' }" [tooltip]="{ show: true }"
              [animation]="{ enabled: true }">
            </app-comprehensive-bar-chart>
            }
            } @else {
            <!-- View Graph Button (Floating Mode) -->
            <div
              class="flex flex-col items-center justify-center p-6 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700/50 transition-all hover:bg-slate-100 dark:hover:bg-slate-800/60 group">
              <div
                class="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-3 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                <app-icon-v2 name="BarChart" [size]="24" />
              </div>
              <p class="text-[13px] font-medium text-slate-600 dark:text-slate-300 mb-4">Chart data is available</p>
              <button (click)="fullscreenChartRequested.emit()"
                class="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold transition-all shadow-lg shadow-blue-500/20 active:scale-95 flex items-center gap-2 cursor-pointer">
                <app-icon-v2 name="Maximize" [size]="16" />
                View Graph
              </button>
            </div>
            }
          </div>
          }
          }
        </div>
      </div>
    </div>
    } @else if (msg.from === 'agent') {
    <!-- Agent Message -->
    <div class="flex items-start gap-3 flex-row-reverse">
      <div
        class="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105"
        [ngClass]="{
        'dark:bg-indigo-900/30': !disableDarkTheme(),
        'dark:border-indigo-700': !disableDarkTheme()
      }">
        <app-icon-v2 name="user" class="text-indigo-600 w-6 h-6"
          [ngClass]="{'dark:text-indigo-400': !disableDarkTheme()}" />
      </div>
      <div class="flex-1 flex flex-col items-end">
        <div class="flex items-center gap-2 mb-1">
          @if(msg.timestamp) {
          <span class="text-[10px] text-gray-400 font-medium mr-1"
            [ngClass]="{'dark:text-gray-500': !disableDarkTheme()}">{{ msg.timestamp | date:'shortTime'
            }}</span>
          }
          <span class="text-sm font-semibold text-indigo-600"
            [ngClass]="{'dark:text-indigo-400': !disableDarkTheme()}">{{ msg.speakerName ? msg.speakerName : 'Agent' }}</span>
        </div>
        <div
          class="bg-white rounded-2xl rounded-tr-none px-4 py-3 shadow-sm border border-gray-100 transition-all hover:shadow-md max-w-[85%]"
          [ngClass]="{
            'dark:bg-indigo-950/40': !disableDarkTheme(),
            'dark:border-indigo-900/50': !disableDarkTheme()
        }">
          <p class="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"
            [ngClass]="{'dark:text-indigo-100': !disableDarkTheme()}">{{ msg.text }}</p>
        </div>
      </div>
    </div>
    } @else {
    <!-- User Message -->
    <div class="flex items-start gap-3 flex-row-reverse">
      <div
        class="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105"
        [ngClass]="{
        'dark:bg-gray-800': !disableDarkTheme(),
        'dark:border-gray-700': !disableDarkTheme()
      }">
        <app-icon-v2 name="user" class="text-gray-500 w-6 h-6"
          [ngClass]="{'dark:text-gray-400': !disableDarkTheme()}" />
      </div>
      <div class="flex-1 flex flex-col items-end">
        <div class="flex items-center gap-2 mb-1">
          @if(msg.timestamp) {
          <span class="text-[10px] text-gray-400 font-medium mr-1"
            [ngClass]="{'dark:text-gray-500': !disableDarkTheme()}">{{ msg.timestamp | date:'shortTime'
            }}</span>
          }
          <span class="text-sm font-semibold text-gray-700" [ngClass]="{'dark:text-gray-300': !disableDarkTheme()}">{{
            msg.speakerName || msg.clientName
            }}</span>
        </div>
        <div
          class="bg-gray-50 rounded-2xl rounded-tr-none px-4 py-3 shadow-sm border border-gray-100 transition-all hover:shadow-md max-w-[85%]"
          [ngClass]="{
            'dark:bg-gray-800/80': !disableDarkTheme(),
            'dark:border-gray-700': !disableDarkTheme()
        }">
          <p class="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"
            [ngClass]="{'dark:text-gray-200': !disableDarkTheme()}">{{ msg.text }}</p>
        </div>
      </div>
    </div>
    }
  </div>
  }

  <!-- Typing Indicator - Only show when waiting for response -->
  @if (isTyping() && (!sequentialRevelation() || activeRevealIndex() === messages().length - 1)) {
  <div class="flex items-start gap-3">
    <div
      class="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0 shadow-sm">
      <app-icon-v2 name="bot" class="text-white w-6 h-6" />
    </div>
    <div class="flex-1">
      <div class="flex items-center gap-2 mb-1">
        <span class="text-sm font-semibold text-[#4F46E5]">AI Assistant</span>
      </div>
      <div class="bg-[#F8FAFF] rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-[#E0E7FF]"
        [ngClass]="{'dark:bg-surface-2': !disableDarkTheme()}">
        <div class="flex items-center gap-2">
          <div class="flex gap-1">
            <span class="w-2 h-2 bg-[#818CF8] rounded-full animate-bounce" style="animation-delay: 0ms;"></span>
            <span class="w-2 h-2 bg-[#818CF8] rounded-full animate-bounce" style="animation-delay: 150ms;"></span>
            <span class="w-2 h-2 bg-[#818CF8] rounded-full animate-bounce" style="animation-delay: 300ms;"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
  }
</ng-template>